import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { searchableUserMenuStyle } from '../searchableUserMenu'

export interface AnchoredSelectOption<T extends string = string> {
  value: T
  label: string
  description?: string
}

interface Props<T extends string> {
  value: T
  options: AnchoredSelectOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function AnchoredSelect<T extends string>({ value, options, onChange, ariaLabel, placeholder = 'Selecione', disabled = false, className = '' }: Props<T>) {
  const [open, setOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const selected = options.find(option => option.value === value)

  useEffect(() => {
    if (!open) return

    function positionMenu() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const bodyZoom = Number.parseFloat(window.getComputedStyle(document.body).zoom) || 1
      setMenuStyle(searchableUserMenuStyle(rect, { width: window.innerWidth, height: window.innerHeight }, bodyZoom))
    }

    function closeOutside(event: PointerEvent) {
      if (!(event.target instanceof Node)) return
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false)
    }

    function closeWithEscape(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }

    positionMenu()
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeWithEscape)
    window.addEventListener('resize', positionMenu)
    window.addEventListener('scroll', positionMenu, true)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeWithEscape)
      window.removeEventListener('resize', positionMenu)
      window.removeEventListener('scroll', positionMenu, true)
    }
  }, [open])

  function toggle() {
    if (!disabled) setOpen(current => !current)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!open) setOpen(true)
    }
  }

  function select(option: AnchoredSelectOption<T>) {
    onChange(option.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  return <div className={`anchored-select ${className}`.trim()}>
    <button ref={triggerRef} type="button" className="anchored-select-trigger" disabled={disabled} aria-label={ariaLabel} aria-haspopup="listbox" aria-controls={listboxId} aria-expanded={open} onClick={toggle} onKeyDown={handleKeyDown}>
      <span>{selected?.label ?? placeholder}</span><span aria-hidden="true">⌄</span>
    </button>
    {open && createPortal(<div ref={menuRef} id={listboxId} role="listbox" aria-label={ariaLabel} className="anchored-select-menu" style={menuStyle}>
      {options.map(option => <button type="button" role="option" aria-selected={option.value === value} className={option.value === value ? 'selected' : ''} key={option.value} onClick={() => select(option)}><span><strong>{option.label}</strong>{option.description && <small>{option.description}</small>}</span>{option.value === value && <i aria-hidden="true">✓</i>}</button>)}
    </div>, document.body)}
  </div>
}
